import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  Pencil,
  Plus,
  Trash2,
  UserPlus,
} from 'lucide-react';

import { listEmployees } from '@/api/employees.api';
import { formatSopIdentifier, listSops } from '@/api/sops.api';
import {
  createChecklistInstance,
  createChecklistTemplate,
  deactivateChecklistTemplate,
  listChecklistInstances,
  listChecklistTemplates,
  listCourses,
  setChecklistItemStatus,
  updateChecklistTemplate,
  type ChecklistInstance,
  type ChecklistItemKind,
  type ChecklistTemplate,
  type ChecklistTemplateItemInput,
} from '@/api/trainingAdmin.api';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { describeTrainingError } from '@/lib/training/errors';
import { useToast } from '@/hooks/useToast';
import { TrainingNoAccessCard } from './_chrome';
import { useTrainingAdminGate } from './_gate';

/**
 * Onboarding checklists (ADR-025). Two audiences on one page, mirroring
 * the server gates: template authoring needs `manage_training`; the
 * per-hire instance board (create / read / complete / waive) needs
 * `view_training_reports`. Superadmins hold both implicitly.
 *
 * Course and sop_ack items auto-complete from training records; only
 * manual items are completed/waived by hand here.
 */

const SELECT_CLS =
  'w-full rounded-kw-md border border-kw-border bg-white px-2.5 py-2 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20';

const KIND_LABEL: Record<ChecklistItemKind, string> = {
  course: 'Course',
  sop_ack: 'SOP ack',
  manual: 'Manual',
};

export function ChecklistsPage() {
  const { canManage, canViewReports } = useTrainingAdminGate();

  if (!canManage && !canViewReports) {
    return <TrainingNoAccessCard capability="manage_training or view_training_reports" />;
  }

  return (
    <div className="mx-auto max-w-4xl space-y-8">
      <div>
        <h1 className="typo-h1">Onboarding checklists</h1>
        <p className="mt-1 text-[14px] text-kw-muted">
          Templates define the steps; instances track one hire each. Course and SOP items complete
          automatically from training records.
        </p>
      </div>

      <TemplatesSection canManage={canManage} canViewReports={canViewReports} />
      {canViewReports ? <InstancesSection /> : null}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Templates
// ─────────────────────────────────────────────────────────────────────────

function TemplatesSection({ canManage, canViewReports }: { canManage: boolean; canViewReports: boolean }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [editorTemplate, setEditorTemplate] = useState<ChecklistTemplate | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [assigning, setAssigning] = useState<ChecklistTemplate | null>(null);

  const templatesQuery = useQuery({
    queryKey: ['training-admin', 'checklist-templates'],
    queryFn: () => listChecklistTemplates({ includeInactive: canManage }),
  });

  const deactivateMutation = useMutation({
    mutationFn: (template: ChecklistTemplate) =>
      deactivateChecklistTemplate(template.id, 'Template retired from the checklists page'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['training-admin', 'checklist-templates'] });
      toast.show('Template deactivated', { variant: 'success' });
    },
    onError: (err) => {
      toast.show(describeTrainingError(err, 'Could not deactivate the template.'), { variant: 'danger' });
    },
  });

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h2 className="text-[13px] font-medium uppercase tracking-wide text-kw-muted">Templates</h2>
        {canManage ? (
          <Button
            size="sm"
            onClick={() => {
              setEditorTemplate(null);
              setEditorOpen(true);
            }}
          >
            <Plus className="size-3.5" />
            New template
          </Button>
        ) : null}
      </div>

      {templatesQuery.isLoading ? (
        <Card className="p-6 text-center text-[13px] text-kw-muted">Loading templates…</Card>
      ) : templatesQuery.error ? (
        <Card className="flex items-start gap-3 border-kw-danger-text/30 bg-kw-danger-bg p-4">
          <AlertTriangle className="size-5 shrink-0 text-kw-danger-text" />
          <p className="text-[13px] text-kw-danger-text">
            {describeTrainingError(templatesQuery.error, 'Could not load templates.')}
          </p>
        </Card>
      ) : (templatesQuery.data ?? []).length === 0 ? (
        <Card className="p-8 text-center text-[13px] text-kw-muted">
          No templates yet{canManage ? ' — create one to standardize onboarding.' : '.'}
        </Card>
      ) : (
        <Card className="p-0">
          <ul className="divide-y divide-kw-border">
            {(templatesQuery.data ?? []).map((template) => (
              <li key={template.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <p className="text-[14px] font-medium text-kw-shark">{template.name}</p>
                  <p className="mt-0.5 text-[12px] text-kw-muted">
                    {template.items.length} item{template.items.length === 1 ? '' : 's'}
                    {template._count ? ` · ${String(template._count.instances)} hires` : ''}
                  </p>
                </div>
                {!template.isActive ? <Badge variant="neutral">Inactive</Badge> : null}
                {canViewReports && template.isActive ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setAssigning(template);
                    }}
                  >
                    <UserPlus className="size-3.5" />
                    Assign to hire
                  </Button>
                ) : null}
                {canManage ? (
                  <>
                    <button
                      type="button"
                      aria-label={`Edit ${template.name}`}
                      onClick={() => {
                        setEditorTemplate(template);
                        setEditorOpen(true);
                      }}
                      className="rounded p-1 text-kw-muted hover:bg-kw-bg hover:text-kw-shark"
                    >
                      <Pencil className="size-3.5" />
                    </button>
                    {template.isActive ? (
                      <button
                        type="button"
                        aria-label={`Deactivate ${template.name}`}
                        disabled={deactivateMutation.isPending}
                        onClick={() => {
                          deactivateMutation.mutate(template);
                        }}
                        className="rounded p-1 text-kw-muted hover:bg-kw-bg hover:text-kw-danger-text"
                      >
                        <Trash2 className="size-3.5" />
                      </button>
                    ) : null}
                  </>
                ) : null}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {editorOpen ? (
        <TemplateEditorModal
          editing={editorTemplate}
          onClose={() => {
            setEditorOpen(false);
            setEditorTemplate(null);
          }}
          onSaved={() => {
            void queryClient.invalidateQueries({ queryKey: ['training-admin', 'checklist-templates'] });
            toast.show(editorTemplate ? 'Template updated' : 'Template created', { variant: 'success' });
          }}
        />
      ) : null}

      {assigning ? (
        <AssignHireModal
          template={assigning}
          onClose={() => {
            setAssigning(null);
          }}
          onSaved={() => {
            void queryClient.invalidateQueries({ queryKey: ['training-admin', 'checklist-instances'] });
            void queryClient.invalidateQueries({ queryKey: ['training-admin', 'checklist-templates'] });
            toast.show('Checklist started for the hire', { variant: 'success' });
          }}
        />
      ) : null}
    </section>
  );
}

interface ItemDraft {
  key: string;
  kind: ChecklistItemKind;
  title: string;
  courseId: string;
  sopId: string;
  dueInDays: string;
}

function newItemDraft(): ItemDraft {
  return {
    key: `item-${Math.random().toString(36).slice(2, 9)}`,
    kind: 'manual',
    title: '',
    courseId: '',
    sopId: '',
    dueInDays: '',
  };
}

function TemplateEditorModal({
  editing,
  onClose,
  onSaved,
}: {
  editing: ChecklistTemplate | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(editing?.name ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');
  const [reason, setReason] = useState('');
  const [items, setItems] = useState<ItemDraft[]>(
    editing
      ? editing.items.map((item) => ({
          key: item.id,
          kind: item.kind,
          title: item.title,
          courseId: item.courseId ?? '',
          sopId: item.sopId ?? '',
          dueInDays: item.dueInDays !== null ? String(item.dueInDays) : '',
        }))
      : [newItemDraft()],
  );
  const [error, setError] = useState<string | null>(null);

  const coursesQuery = useQuery({ queryKey: ['training-admin', 'courses'], queryFn: listCourses });
  const sopsQuery = useQuery({ queryKey: ['compliance-sops'], queryFn: listSops });

  const mutation = useMutation({
    mutationFn: () => {
      const itemInputs = items.map(
        (item): ChecklistTemplateItemInput => ({
          kind: item.kind,
          title: item.title.trim(),
          ...(item.kind === 'course' ? { courseId: item.courseId } : {}),
          ...(item.kind === 'sop_ack' ? { sopId: item.sopId } : {}),
          dueInDays: item.dueInDays.trim() === '' ? null : Number(item.dueInDays),
        }),
      );
      const base = {
        name: name.trim(),
        description: description.trim() ? description.trim() : null,
        items: itemInputs,
      };
      return editing
        ? updateChecklistTemplate(editing.id, { ...base, reasonForChange: reason.trim() })
        : createChecklistTemplate(base);
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
    onError: (err) => {
      setError(describeTrainingError(err, 'Could not save the template.'));
    },
  });

  function patchItem(index: number, patch: Partial<ItemDraft>): void {
    setItems((prev) => prev.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  }

  function moveItem(index: number, delta: -1 | 1): void {
    setItems((prev) => {
      const target = index + delta;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      const a = next[index];
      const b = next[target];
      if (a === undefined || b === undefined) return prev;
      next[index] = b;
      next[target] = a;
      return next;
    });
  }

  const valid =
    name.trim().length > 0 &&
    (editing === null || reason.trim().length > 0) &&
    items.every(
      (item) =>
        item.title.trim().length > 0 &&
        (item.kind !== 'course' || item.courseId !== '') &&
        (item.kind !== 'sop_ack' || item.sopId !== ''),
    );

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (!valid) return;
    setError(null);
    mutation.mutate();
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={editing ? 'Edit template' : 'New template'}
      size="lg"
      className="max-h-[90vh] overflow-y-auto"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input
            label="Name"
            name="template-name"
            required
            value={name}
            onChange={(e) => {
              setName(e.target.value);
            }}
            placeholder="e.g. Warehouse new hire"
          />
          <Input
            label="Description (optional)"
            name="template-description"
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
            }}
          />
        </div>

        <div className="space-y-2">
          <span className="text-[13px] font-medium text-kw-label">Items (in order)</span>
          {items.map((item, index) => (
            <div key={item.key} className="space-y-2 rounded-kw-md border border-kw-border p-3">
              <div className="flex items-center gap-2">
                <div className="flex flex-col">
                  <button
                    type="button"
                    aria-label={`Move item ${String(index + 1)} up`}
                    disabled={index === 0}
                    onClick={() => {
                      moveItem(index, -1);
                    }}
                    className="rounded p-0.5 text-kw-muted hover:bg-kw-bg disabled:opacity-30"
                  >
                    <ArrowUp className="size-3.5" />
                  </button>
                  <button
                    type="button"
                    aria-label={`Move item ${String(index + 1)} down`}
                    disabled={index === items.length - 1}
                    onClick={() => {
                      moveItem(index, 1);
                    }}
                    className="rounded p-0.5 text-kw-muted hover:bg-kw-bg disabled:opacity-30"
                  >
                    <ArrowDown className="size-3.5" />
                  </button>
                </div>
                <select
                  value={item.kind}
                  aria-label={`Kind of item ${String(index + 1)}`}
                  onChange={(e) => {
                    const v = e.target.value;
                    patchItem(index, {
                      kind: v === 'course' || v === 'sop_ack' ? v : 'manual',
                    });
                  }}
                  className={`${SELECT_CLS} w-32 shrink-0`}
                >
                  <option value="manual">Manual</option>
                  <option value="course">Course</option>
                  <option value="sop_ack">SOP ack</option>
                </select>
                <input
                  type="text"
                  aria-label={`Title of item ${String(index + 1)}`}
                  value={item.title}
                  onChange={(e) => {
                    patchItem(index, { title: e.target.value });
                  }}
                  placeholder="Item title"
                  className="min-w-0 flex-1 rounded-kw-md border border-kw-border bg-white px-2.5 py-2 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
                />
                <button
                  type="button"
                  aria-label={`Remove item ${String(index + 1)}`}
                  disabled={items.length === 1}
                  onClick={() => {
                    setItems((prev) => prev.filter((_, i) => i !== index));
                  }}
                  className="rounded p-1 text-kw-muted hover:bg-kw-bg hover:text-kw-danger-text disabled:opacity-30"
                >
                  <Trash2 className="size-4" />
                </button>
              </div>

              <div className="flex flex-wrap items-center gap-2 pl-8">
                {item.kind === 'course' ? (
                  <select
                    value={item.courseId}
                    aria-label={`Course for item ${String(index + 1)}`}
                    onChange={(e) => {
                      patchItem(index, { courseId: e.target.value });
                    }}
                    className={`${SELECT_CLS} max-w-xs`}
                  >
                    <option value="">Select a course…</option>
                    {(coursesQuery.data ?? []).map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.title}
                      </option>
                    ))}
                  </select>
                ) : null}
                {item.kind === 'sop_ack' ? (
                  <select
                    value={item.sopId}
                    aria-label={`SOP for item ${String(index + 1)}`}
                    onChange={(e) => {
                      patchItem(index, { sopId: e.target.value });
                    }}
                    className={`${SELECT_CLS} max-w-xs`}
                  >
                    <option value="">Select an SOP…</option>
                    {(sopsQuery.data ?? []).map((sop) => (
                      <option key={sop.id} value={sop.id}>
                        {formatSopIdentifier(sop)} — {sop.title}
                      </option>
                    ))}
                  </select>
                ) : null}
                <input
                  type="number"
                  min={0}
                  max={3650}
                  aria-label={`Due in days for item ${String(index + 1)}`}
                  value={item.dueInDays}
                  onChange={(e) => {
                    patchItem(index, { dueInDays: e.target.value });
                  }}
                  placeholder="Due in days"
                  className="w-28 rounded-kw-md border border-kw-border bg-white px-2.5 py-2 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
                />
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={() => {
              setItems((prev) => [...prev, newItemDraft()]);
            }}
            className="text-[12px] font-medium text-kw-cerulean hover:underline"
          >
            + Add item
          </button>
        </div>

        {editing ? (
          <Input
            label="Reason for change (audit log)"
            name="template-reason"
            required
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
            }}
            placeholder="e.g. Added forklift certification step"
          />
        ) : null}

        {error ? (
          <div className="flex items-start gap-2 rounded-kw-md border border-kw-danger-text/30 bg-kw-danger-bg p-3">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-danger-text" />
            <p className="text-[12px] text-kw-danger-text">{error}</p>
          </div>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={mutation.isPending} disabled={!valid}>
            {editing ? 'Save template' : 'Create template'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function AssignHireModal({
  template,
  onClose,
  onSaved,
}: {
  template: ChecklistTemplate;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [employeeId, setEmployeeId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const employeesQuery = useQuery({
    queryKey: ['employees', 'for-training-admin'],
    queryFn: () => listEmployees({ status: 'active', limit: 500 }),
  });

  const mutation = useMutation({
    mutationFn: () => createChecklistInstance({ templateId: template.id, employeeId }),
    onSuccess: () => {
      onSaved();
      onClose();
    },
    onError: (err) => {
      setError(describeTrainingError(err, 'Could not start the checklist.'));
    },
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (!employeeId) return;
    setError(null);
    mutation.mutate();
  }

  return (
    <Modal open onClose={onClose} title={`Assign "${template.name}"`} size="sm">
      <form onSubmit={handleSubmit} className="space-y-4">
        <label className="flex flex-col gap-1">
          <span className="text-[13px] font-medium text-kw-label">Employee</span>
          <select
            value={employeeId}
            onChange={(e) => {
              setEmployeeId(e.target.value);
            }}
            className={SELECT_CLS}
          >
            <option value="">Select an employee…</option>
            {(employeesQuery.data?.data ?? []).map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.firstName} {emp.lastName}
                {emp.jobTitle ? ` — ${emp.jobTitle}` : ''}
              </option>
            ))}
          </select>
          <span className="text-[12px] text-kw-muted">
            Items already on record (finished courses, signed SOPs) complete automatically.
          </span>
        </label>
        {error ? <p className="text-[12px] text-kw-danger-text">{error}</p> : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={mutation.isPending} disabled={!employeeId}>
            Start checklist
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Instances (per-hire board)
// ─────────────────────────────────────────────────────────────────────────

function InstancesSection() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const instancesQuery = useQuery({
    queryKey: ['training-admin', 'checklist-instances'],
    queryFn: () => listChecklistInstances(),
  });

  const itemMutation = useMutation({
    mutationFn: (vars: { instanceId: string; itemId: string; status: 'complete' | 'waived' }) =>
      setChecklistItemStatus(vars.instanceId, vars.itemId, { status: vars.status }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['training-admin', 'checklist-instances'] });
    },
    onError: (err) => {
      toast.show(describeTrainingError(err, 'Could not update the item.'), { variant: 'danger' });
    },
  });

  return (
    <section className="space-y-2">
      <h2 className="text-[13px] font-medium uppercase tracking-wide text-kw-muted">Hires in progress</h2>

      {instancesQuery.isLoading ? (
        <Card className="p-6 text-center text-[13px] text-kw-muted">Loading checklists…</Card>
      ) : instancesQuery.error ? (
        <Card className="flex items-start gap-3 border-kw-danger-text/30 bg-kw-danger-bg p-4">
          <AlertTriangle className="size-5 shrink-0 text-kw-danger-text" />
          <p className="text-[13px] text-kw-danger-text">
            {describeTrainingError(instancesQuery.error, 'Could not load checklist instances.')}
          </p>
        </Card>
      ) : (instancesQuery.data ?? []).length === 0 ? (
        <Card className="flex flex-col items-center gap-3 p-10 text-center">
          <ClipboardCheck className="size-10 text-kw-ghost" />
          <p className="text-[13px] text-kw-muted">
            No hire checklists yet — assign a template to an employee above.
          </p>
        </Card>
      ) : (
        <Card className="p-0">
          <ul className="divide-y divide-kw-border">
            {(instancesQuery.data ?? []).map((instance) => (
              <InstanceRow
                key={instance.id}
                instance={instance}
                expanded={expandedId === instance.id}
                onToggle={() => {
                  setExpandedId((prev) => (prev === instance.id ? null : instance.id));
                }}
                mutating={itemMutation.isPending}
                onSetStatus={(itemId, status) => {
                  itemMutation.mutate({ instanceId: instance.id, itemId, status });
                }}
              />
            ))}
          </ul>
        </Card>
      )}
    </section>
  );
}

function InstanceRow({
  instance,
  expanded,
  onToggle,
  mutating,
  onSetStatus,
}: {
  instance: ChecklistInstance;
  expanded: boolean;
  onToggle: () => void;
  mutating: boolean;
  onSetStatus: (itemId: string, status: 'complete' | 'waived') => void;
}) {
  const doneCount = instance.items.filter((item) => item.status !== 'pending').length;

  return (
    <li>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="flex w-full flex-wrap items-center gap-3 px-4 py-3 text-left hover:bg-kw-bg"
      >
        {expanded ? (
          <ChevronDown className="size-4 shrink-0 text-kw-muted" />
        ) : (
          <ChevronRight className="size-4 shrink-0 text-kw-muted" />
        )}
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-medium text-kw-shark">
            {instance.employee.firstName} {instance.employee.lastName}
          </p>
          <p className="mt-0.5 text-[12px] text-kw-muted">
            {instance.templateNameSnapshot} · started {new Date(instance.createdAt).toLocaleDateString()}
          </p>
        </div>
        <span className="text-[12px] text-kw-muted">
          {doneCount}/{instance.items.length} done
        </span>
        {instance.completedAt ? (
          <Badge variant="success">Complete</Badge>
        ) : (
          <Badge variant="info">In progress</Badge>
        )}
      </button>

      {expanded ? (
        <ul className="space-y-1 border-t border-kw-border bg-kw-bg/50 px-4 py-3 pl-11">
          {instance.items.map((item) => (
            <li key={item.id} className="flex flex-wrap items-center gap-2 py-1">
              <Badge variant="neutral">{KIND_LABEL[item.kind]}</Badge>
              <span className="min-w-0 flex-1 text-[13px] text-kw-shark">{item.title}</span>
              {item.status === 'complete' ? (
                <Badge variant="success">Complete</Badge>
              ) : item.status === 'waived' ? (
                <Badge variant="warning">Waived</Badge>
              ) : item.kind === 'manual' ? (
                <span className="flex items-center gap-1.5">
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={mutating}
                    onClick={() => {
                      onSetStatus(item.id, 'complete');
                    }}
                  >
                    Complete
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={mutating}
                    onClick={() => {
                      onSetStatus(item.id, 'waived');
                    }}
                  >
                    Waive
                  </Button>
                </span>
              ) : (
                <Badge variant="neutral">Pending</Badge>
              )}
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}
